/**
 * useWorkspace - React hook for workspace management
 * Provides easy access to workspace operations and state
 */

import { useState, useEffect, useCallback } from 'react';
import { WorkspaceLayoutService } from '../services/WorkspaceLayoutService';
import type {
  WorkspaceLayout,
  RepositoryWorkspaceState,
  CreateWorkspaceOptions,
  UpdateWorkspaceOptions,
} from '../types/workspace.types';

export interface UseWorkspaceOptions {
  /**
   * Optional repository key for repository-specific state
   * If provided, the hook will manage repository state
   */
  repositoryKey?: string;

  /**
   * Auto-initialize repository state if it doesn't exist
   */
  autoInitialize?: boolean;

  /**
   * Default workspace to use for auto-initialization
   */
  defaultWorkspaceId?: string;
}

export interface UseWorkspaceReturn {
  /**
   * All available workspaces (built-in + user-created)
   */
  workspaces: Record<string, WorkspaceLayout>;

  /**
   * Current repository state (if repositoryKey provided)
   */
  repositoryState: RepositoryWorkspaceState | null;

  /**
   * Current active workspace (if repositoryKey provided)
   */
  activeWorkspace: WorkspaceLayout | null;

  /**
   * Loading state
   */
  loading: boolean;

  /**
   * Error state
   */
  error: Error | null;

  /**
   * Get a specific workspace by ID
   */
  getWorkspace: (id: string) => Promise<WorkspaceLayout | null>;

  /**
   * Create a new workspace
   */
  createWorkspace: (options: CreateWorkspaceOptions) => Promise<WorkspaceLayout>;

  /**
   * Update an existing workspace
   */
  updateWorkspace: (
    id: string,
    updates: UpdateWorkspaceOptions,
  ) => Promise<WorkspaceLayout | null>;

  /**
   * Delete a workspace
   */
  deleteWorkspace: (id: string) => Promise<boolean>;

  /**
   * Apply a workspace to the current repository
   */
  applyWorkspace: (workspaceId: string) => Promise<void>;

  /**
   * Update repository sizes
   */
  updateSizes: (sizes: {
    left: number;
    middle: number;
    right: number;
  }) => Promise<void>;

  /**
   * Update repository collapsed state
   */
  updateCollapsed: (collapsed: {
    left?: boolean;
    right?: boolean;
  }) => Promise<void>;

  /**
   * Reset repository to workspace defaults
   */
  resetToDefaults: () => Promise<void>;

  /**
   * Refresh workspace data
   */
  refresh: () => Promise<void>;
}

export function useWorkspace(
  options: UseWorkspaceOptions = {},
): UseWorkspaceReturn {
  const { repositoryKey, autoInitialize = false, defaultWorkspaceId } = options;

  const [workspaces, setWorkspaces] = useState<Record<string, WorkspaceLayout>>(
    {},
  );
  const [repositoryState, setRepositoryState] =
    useState<RepositoryWorkspaceState | null>(null);
  const [activeWorkspace, setActiveWorkspace] =
    useState<WorkspaceLayout | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Load all workspaces and repository state
   */
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load all workspaces
      const allWorkspaces =
        await WorkspaceLayoutService.getWorkspaceLayouts();
      setWorkspaces(allWorkspaces);

      // Load repository state if repositoryKey provided
      if (repositoryKey) {
        let repoState =
          await WorkspaceLayoutService.getRepositoryState(repositoryKey);

        // Auto-initialize if requested and state doesn't exist
        if (!repoState && autoInitialize) {
          const workspaceId =
            defaultWorkspaceId || 'project-management'; // Default to project-management
          const workspace =
            await WorkspaceLayoutService.getWorkspaceLayout(workspaceId);

          if (workspace) {
            repoState = {
              workspaceId,
              sizes: workspace.defaultSizes || {
                left: 20,
                middle: 45,
                right: 35,
              },
              collapsed: workspace.defaultCollapsed || {
                left: false,
                right: false,
              },
            };

            await WorkspaceLayoutService.setRepositoryState(
              repositoryKey,
              repoState,
            );
          }
        }

        setRepositoryState(repoState);

        // Load active workspace if repository has one
        if (repoState?.workspaceId) {
          const workspace = await WorkspaceLayoutService.getWorkspaceLayout(
            repoState.workspaceId,
          );
          setActiveWorkspace(workspace);
        } else {
          setActiveWorkspace(null);
        }
      }
    } catch (err) {
      setError(err as Error);
      console.error('Failed to load workspace data:', err);
    } finally {
      setLoading(false);
    }
  }, [repositoryKey, autoInitialize, defaultWorkspaceId]);

  /**
   * Initial load
   */
  useEffect(() => {
    loadData();
  }, [loadData]);

  /**
   * Get a specific workspace
   */
  const getWorkspace = useCallback(
    async (id: string) => {
      return await WorkspaceLayoutService.getWorkspaceLayout(id);
    },
    [],
  );

  /**
   * Create a new workspace
   */
  const createWorkspace = useCallback(
    async (createOptions: CreateWorkspaceOptions) => {
      const workspace =
        await WorkspaceLayoutService.createWorkspaceLayout(createOptions);
      await loadData(); // Refresh
      return workspace;
    },
    [loadData],
  );

  /**
   * Update an existing workspace
   */
  const updateWorkspace = useCallback(
    async (id: string, updates: UpdateWorkspaceOptions) => {
      const workspace =
        await WorkspaceLayoutService.updateWorkspaceLayout(id, updates);
      await loadData(); // Refresh
      return workspace;
    },
    [loadData],
  );

  /**
   * Delete a workspace
   */
  const deleteWorkspace = useCallback(
    async (id: string) => {
      const success = await WorkspaceLayoutService.deleteWorkspaceLayout(id);
      if (success) {
        await loadData(); // Refresh
      }
      return success;
    },
    [loadData],
  );

  /**
   * Apply a workspace to the current repository
   */
  const applyWorkspace = useCallback(
    async (workspaceId: string) => {
      if (!repositoryKey) {
        console.error('Cannot apply workspace without repositoryKey');
        return;
      }

      const workspace =
        await WorkspaceLayoutService.getWorkspaceLayout(workspaceId);
      if (!workspace) {
        console.error(`Workspace ${workspaceId} not found`);
        return;
      }

      const newState: RepositoryWorkspaceState = {
        workspaceId,
        sizes: workspace.defaultSizes || { left: 20, middle: 45, right: 35 },
        collapsed: workspace.defaultCollapsed || {
          left: false,
          right: false,
        },
      };

      await WorkspaceLayoutService.setRepositoryState(repositoryKey, newState);
      await loadData(); // Refresh
    },
    [repositoryKey, loadData],
  );

  /**
   * Update repository sizes
   */
  const updateSizes = useCallback(
    async (sizes: { left: number; middle: number; right: number }) => {
      if (!repositoryKey) {
        console.error('Cannot update sizes without repositoryKey');
        return;
      }

      await WorkspaceLayoutService.updateRepositorySizes(repositoryKey, sizes);
      await loadData(); // Refresh
    },
    [repositoryKey, loadData],
  );

  /**
   * Update repository collapsed state
   */
  const updateCollapsed = useCallback(
    async (collapsed: { left?: boolean; right?: boolean }) => {
      if (!repositoryKey) {
        console.error('Cannot update collapsed without repositoryKey');
        return;
      }

      await WorkspaceLayoutService.updateRepositoryCollapsed(
        repositoryKey,
        collapsed,
      );
      await loadData(); // Refresh
    },
    [repositoryKey, loadData],
  );

  /**
   * Reset repository to workspace defaults
   */
  const resetToDefaults = useCallback(async () => {
    if (!repositoryKey || !repositoryState?.workspaceId) {
      console.error('Cannot reset without repositoryKey and active workspace');
      return;
    }

    await WorkspaceLayoutService.resetRepositoryToWorkspaceDefaults(
      repositoryKey,
      repositoryState.workspaceId,
    );
    await loadData(); // Refresh
  }, [repositoryKey, repositoryState?.workspaceId, loadData]);

  /**
   * Refresh workspace data
   */
  const refresh = useCallback(async () => {
    await loadData();
  }, [loadData]);

  return {
    workspaces,
    repositoryState,
    activeWorkspace,
    loading,
    error,
    getWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    applyWorkspace,
    updateSizes,
    updateCollapsed,
    resetToDefaults,
    refresh,
  };
}
